import {
  Dialog,
  DialogClose,
  DialogContentBare,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  cn,
} from '@parcel/ui';
import { ChevronLeft, ChevronRight, ExternalLink, Loader2, X } from 'lucide-react';
import * as React from 'react';
import type { ApiProjectImage } from '../../lib/projects.js';
import { AuthenticatedImage, usePreloadImage } from '../auth/authenticated-image.js';
import { imageCreditLine, imageKindLabel, imageSourceHref } from './project-image-gallery.js';

// Full-screen viewer for a project's images: paging wraps, neighbours preload, the rail marks the current one.

export function ImageLightbox({
  images,
  index,
  onIndexChange,
  onClose,
  onImageError,
  returnFocusTo,
}: {
  images: ApiProjectImage[];
  /** null while closed. */
  index: number | null;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  onImageError?: (id: string) => void;
  /** Focus goes back here on close; the dialog has no Radix trigger to return to. */
  returnFocusTo?: HTMLElement | null;
}) {
  const count = images.length;
  const current = index === null ? undefined : images[index];
  const preload = usePreloadImage();
  // The URL painted since this opening, so the spinner never outlives or precedes its image.
  const [loadedUrl, setLoadedUrl] = React.useState<string | null>(null);
  if (index === null && loadedUrl !== null) setLoadedUrl(null);
  const loading = current !== undefined && loadedUrl !== current.url;
  const step = (delta: number) => {
    if (index === null || count < 2) return;
    onIndexChange((index + delta + count) % count);
  };

  React.useEffect(() => {
    if (index === null || count < 2) return;
    for (const delta of [1, -1]) {
      const neighbour = images[(index + delta + count) % count];
      if (neighbour) preload(neighbour.url);
    }
  }, [count, images, index, preload]);

  if (index === null || !current) return null;

  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogPortal>
        <DialogOverlay className="bg-black/90" />
        <DialogContentBare
          aria-describedby={undefined}
          onCloseAutoFocus={(event) => {
            if (!returnFocusTo?.isConnected) return;
            event.preventDefault();
            returnFocusTo.focus();
          }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowRight') step(1);
            else if (event.key === 'ArrowLeft') step(-1);
          }}
          className="fixed inset-0 z-50 flex flex-col text-white outline-none lg:flex-row"
        >
          <DialogTitle className="sr-only">
            {`${imageKindLabel(current.kind)} ${index + 1} of ${count}`}
          </DialogTitle>
          {/* biome-ignore lint/a11y/useKeyWithClickEvents: a click on the dark area is a mouse shortcut; Esc closes from the keyboard. */}
          <div
            data-lightbox-stage
            onClick={(event) => {
              if (event.target === event.currentTarget) onClose();
            }}
            className="relative flex min-h-0 flex-1 flex-col items-center justify-center p-4 lg:p-8"
          >
            <DialogClose
              aria-label="Close"
              className="absolute right-4 top-4 z-10 rounded-full bg-black/50 p-2 hover:bg-black/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <X className="h-5 w-5" />
            </DialogClose>
            <span className="absolute left-4 top-4 rounded-full bg-black/50 px-3 py-1 text-sm tabular-nums">
              {`${index + 1} / ${count}`}
            </span>
            {count > 1 ? (
              <>
                <PageArrow label="Previous image" side="left" onClick={() => step(-1)} />
                <PageArrow label="Next image" side="right" onClick={() => step(1)} />
              </>
            ) : null}
            <div
              data-lightbox-current
              data-loading={loading ? '' : undefined}
              className="pointer-events-none relative flex max-h-[80vh] w-full items-center justify-center"
            >
              {loading ? (
                <Loader2 className="absolute h-8 w-8 animate-spin text-white/70" aria-hidden />
              ) : null}
              <AuthenticatedImage
                key={current.url}
                src={current.url}
                alt={`${imageKindLabel(current.kind)} from ${current.attribution.publisher_name}`}
                className={cn(
                  'pointer-events-auto max-h-[80vh] max-w-full object-contain transition-opacity duration-200',
                  loading ? 'opacity-0' : 'opacity-100',
                )}
                onLoad={() => setLoadedUrl(current.url)}
                onError={() => {
                  setLoadedUrl(current.url);
                  onImageError?.(current.id);
                }}
              />
            </div>
            <p className="mt-3 flex max-w-full items-center gap-2 text-sm text-white/80">
              <span className="font-medium text-white">{imageKindLabel(current.kind)}</span>
              <span className="truncate">{imageCreditLine(current)}</span>
              <a
                href={imageSourceHref(current)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex shrink-0 items-center gap-1 underline-offset-2 hover:underline"
              >
                View source
                <ExternalLink className="h-3.5 w-3.5" aria-hidden />
              </a>
            </p>
          </div>
          {count > 1 ? (
            <nav
              aria-label="All images"
              className="flex shrink-0 gap-2 overflow-x-auto border-t border-white/10 p-3 lg:w-48 lg:flex-col lg:overflow-y-auto lg:border-l lg:border-t-0"
            >
              {images.map((image, i) => (
                <RailThumb
                  key={image.id}
                  image={image}
                  current={i === index}
                  near={Math.min(Math.abs(i - index), count - Math.abs(i - index)) <= 1}
                  label={`${imageKindLabel(image.kind)} ${i + 1} of ${count}`}
                  onSelect={() => onIndexChange(i)}
                />
              ))}
            </nav>
          ) : null}
        </DialogContentBare>
      </DialogPortal>
    </Dialog>
  );
}

/** A rail thumbnail fetches its image only near the current one or once scrolled into view. */
function RailThumb({
  image,
  current,
  near,
  label,
  onSelect,
}: {
  image: ApiProjectImage;
  current: boolean;
  near: boolean;
  label: string;
  onSelect: () => void;
}) {
  const ref = React.useRef<HTMLButtonElement>(null);
  const [seen, setSeen] = React.useState(false);
  React.useEffect(() => {
    const node = ref.current;
    if (seen || !node) return;
    if (typeof IntersectionObserver === 'undefined') {
      setSeen(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) setSeen(true);
      },
      { rootMargin: '200px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [seen]);
  return (
    <button
      ref={ref}
      type="button"
      data-lightbox-thumb
      aria-current={current ? 'true' : undefined}
      aria-label={label}
      onClick={onSelect}
      className={cn(
        'h-16 w-24 shrink-0 overflow-hidden rounded-md bg-white/10 ring-2 lg:h-24 lg:w-full',
        current ? 'ring-white' : 'opacity-60 ring-transparent hover:opacity-100',
      )}
    >
      {seen || near ? (
        <AuthenticatedImage src={image.url} alt="" className="h-full w-full object-cover" />
      ) : null}
    </button>
  );
}

function PageArrow({
  label,
  side,
  onClick,
}: { label: string; side: 'left' | 'right'; onClick: () => void }) {
  const Icon = side === 'left' ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={cn(
        'absolute top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/50 p-2 hover:bg-black/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white',
        side === 'left' ? 'left-4' : 'right-4',
      )}
    >
      <Icon className="h-6 w-6" />
    </button>
  );
}
